import { CarrierVisitPerformanceDto, EquipmentType } from '@operations/app/api'
import { CarrierVisitRefreshEventPayload } from '@operations/app/api/signalRDtos/carrierVisitRefreshEventPayload'
import { WorkInstructionUpsertedEventPayload } from '@operations/app/api/signalRDtos/workInstructionUpsertedEventPayload'
import { EventTypes } from '@operations/messages/eventsTypes'
import carrierVisitService from '@operations/services/carrierVisitService'
import { AppStore, IEvent, MessageBus } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

export class VesselKPIUIStore {
  visitPerformance?: CarrierVisitPerformanceDto
  vesselVisitId?: number

  constructor(
    private readonly messageBus: MessageBus,
    private readonly appStore: AppStore,
  ) {
    makeObservable(this, {
      visitPerformance: observable,
      vesselVisitId: observable,

      setVesselVisitId: action,

      hoursLeft: computed,
      timeOfCompletion: computed,
    })

    this.messageBus.subscribeEvent<WorkInstructionUpsertedEventPayload>(
      EventTypes.WorkInstructionUpserted,
      this.handleWorkInstructionUpsertedEvent,
    )

    this.messageBus.subscribeEvent<CarrierVisitRefreshEventPayload>(
      EventTypes.CarrierVisitRefresh,
      this.handleCarrierVisitRefreshEvent,
    )

    this.messageBus.subscribeEvent<number>(
      EventTypes.CraneSplitGeneratedForVisit,
      this.handleCraneSplitGeneratedForVisitEvent,
    )
  }

  public async getVisitPerformance(carrierVisitId: number) {
    const result = await carrierVisitService.getVisitPerformance(carrierVisitId)

    runInAction(() => {
      this.visitPerformance = result
    })
  }

  public setVesselVisitId(vesselVisitId: number) {
    this.vesselVisitId = vesselVisitId
  }

  public get hoursLeft() {
    if (!this.visitPerformance?.minutesLeft) return 0

    const { minutesLeft } = this.visitPerformance

    return Math.round(minutesLeft / 60)
  }

  public get timeOfCompletion() {
    if (!this.visitPerformance?.minutesLeft) return undefined

    const { minutesLeft } = this.visitPerformance
    const date = new Date()

    return dayjs(date).add(minutesLeft, 'minutes').toDate()
  }

  private readonly handleWorkInstructionUpsertedEvent = async (
    event: IEvent<WorkInstructionUpsertedEventPayload>,
  ) => {
    const payload = event.payload

    if (
      payload?.workInstruction.carrierVisitId === this.vesselVisitId &&
      payload?.newJob?.equipmentType === EquipmentType.Sts
    ) {
      await this.getVisitPerformanceWithouLoader(payload.workInstruction.carrierVisitId)
    }
  }

  private readonly handleCarrierVisitRefreshEvent = async (
    event: IEvent<CarrierVisitRefreshEventPayload>,
  ) => {
    const payload = event.payload

    if (payload?.carrierVisits?.some(cv => cv.carrierVisitId === this.vesselVisitId)) {
      await this.getVisitPerformanceWithouLoader(this.vesselVisitId!)
    }
  }

  private readonly handleCraneSplitGeneratedForVisitEvent = async (event: IEvent<number>) => {
    const visitId = event.payload

    if (this.vesselVisitId === visitId) {
      await this.getVisitPerformanceWithouLoader(this.vesselVisitId!)
    }
  }

  private readonly getVisitPerformanceWithouLoader = async (carrierVisitId: number) => {
    await this.appStore.triggerRequestWithoutLoader(
      async () => await this.getVisitPerformance(carrierVisitId),
    )
  }
}
